// AI Studio Constants
import { ToolConfig } from './types';

// Prompt input
export const PROMPT_DISABLED_PLACEHOLDER = 'Prompt not supported for this tool';
export const PROMPT_MAX_LENGTH = 500;

// Image upload limits
export const MAX_UPLOAD_SIZE_MB = 20;
export const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

// Stricter limit for 3D agents (image-to-3d, multi-views-to-3d)
export const MAX_3D_UPLOAD_SIZE_MB = 10;
export const MAX_3D_UPLOAD_SIZE_BYTES = MAX_3D_UPLOAD_SIZE_MB * 1024 * 1024;

// Tool configurations
// inputPresetType identifies which preset type accepts the product image or 3D capture
export const TOOLS: ToolConfig[] = [
    {
        id: 'virtual-tryon',
        name: 'Virtual Model',
        description:
            'Generate a mockup with a product worn on a person or model, trying different poses, environments, and effects for photorealistic or stylized visuals.',
        inputPresetType: 'clothing',
        guideLink: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio#h_01KHX76W619QVHKJGQZ9DS8BZT',
        promptPlaceholder: 'Add more details about the virtual model experience (optional)...'
    },
    {
        id: 'product-staging',
        name: 'Product Staging',
        description: 'Bring a product to life in realistic environments with context and surface details, or create clean studio visuals.',
        inputPresetType: 'product',
        guideLink: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio#h_01KHX76W641M4EXDWVSTE002NQ',
        promptPlaceholder: 'Add details about the desired scene (optional)...'
    },
    {
        id: 'magic-swap',
        name: 'Product Transformer',
        description: 'Change color, material, or shape of a product or its parts for photorealistic or stylized visuals.',
        inputPresetType: 'product',
        guideLink: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio#h_01KHX76W66FKAXQ2MC0G7ZKBNY',
        promptPlaceholder: 'Describe the product transformation details (optional)...'
    },
    {
        id: 'multi-image-fusion',
        name: 'Multi-Product Scene',
        description:
            'Create scenes with multiple products, controlling environment, surfaces, and effects to generate photorealistic results or clean studio visuals.',
        inputPresetType: 'images',
        guideLink: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio#h_01KHX76W68DSECT7NQA6M6EJTC',
        promptPlaceholder: 'Add details about the multi-product scene (optional)...',
        allowProductMode: false
    },
    {
        id: 'ad-generator',
        name: 'Ad Builder',
        description: 'Create ready-to-publish advertising content.',
        inputPresetType: 'product',
        guideLink: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio#h_01KHX76W6A2XRQYRDZFDP7VQFE',
        promptPlaceholder: 'Add more details about the ad (optional)...'
    },
    {
        id: 'video-generation',
        name: 'Video',
        description: 'Generate videos from a single image with motion, context, and effects.',
        inputPresetType: 'image',
        guideLink: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio#h_01KHX76W6C4PG81XB58NY2EF98',
        promptPlaceholder: 'Add more details about the video (optional)...'
    },
    {
        id: 'upscaler',
        name: 'Upscale',
        description: 'Enhance product images up to 4x resolution with AI-powered detail and clarity.',
        inputPresetType: 'image',
        guideLink: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio#h_01KHX76W6ED9J9T4KV8HC11KFY',
        promptPlaceholder: 'Describe enhancement details (optional)...',
        allowProductMode: false,
        allowPrompt: false
    },
    {
        id: 'text-to-3d',
        name: 'Text to 3D',
        description: 'Generate high-fidelity 3D models and assets directly from simple text prompts.',
        promptPlaceholder: 'Describe your 3D model, see it come to life...',
        allowProductMode: false,
        allowImageInput: false,
        fakeDuration: 180
    },
    {
        id: 'image-to-3d',
        name: 'Image to 3D',
        description: 'Transform 2D images and sketches into detailed 3D geometry instantly.',
        inputPresetType: 'image',
        allowProductMode: false,
        allowPrompt: false,
        fakeDuration: 180
    },
    {
        id: 'multi-views-to-3d',
        name: 'Multi-View to 3D',
        description: 'Create accurate 3D models using 1-8 standard multi-view image references.',
        allowProductMode: false
    }
];

// Tool IDs
export const TOOL_ID = {
    VIRTUAL_TRYON: 'virtual-tryon',
    MAGIC_SWAP: 'magic-swap',
    PRODUCT_STAGING: 'product-staging',
    MULTI_IMAGE_FUSION: 'multi-image-fusion',
    AD_GENERATOR: 'ad-generator',
    VIDEO_GENERATION: 'video-generation',
    UPSCALER: 'upscaler',
    TEXT_TO_3D: 'text-to-3d',
    IMAGE_TO_3D: 'image-to-3d',
    MULTI_VIEWS_TO_3D: 'multi-views-to-3d'
} as const;

// Shared preset category ID used across multiple tools for effects presets
export const SHARED_EFFECTS_CATEGORY_TOOL_ID = 'effects';

// Preset Types
export const PRESET_TYPE = {
    ENVIRONMENT: 'environment',
    EFFECT: 'effect',
    PREDEFINED_IMAGE: 'predefined-image'
} as const;

// Category IDs for virtual-tryon
export const VIRTUAL_TRYON_CATEGORY_ID = {
    PERSON: 24,
    CLOTHING: 25
} as const;

// Category IDs for product-staging
export const PRODUCT_STAGING_CATEGORY_ID = {
    NEUTRAL: 2,
    HOME: 3,
    OUTDOOR: 4,
    BEACH: 5,
    URBAN: 6,
    BACKGROUND: 26,
    PRODUCT: 27,
    FURNITURE: 28,
    ROOM: 29
} as const;
